import { SxProps } from '@mui/system';
import { Colors, inputBackgroundColor, inputPlaceholderColor } from '../../../constants';
import { Theme } from '@mui/material';
import { clsx } from '../../../utils';
import { inputBorderRadius } from '../../inputs/input.styles';
import { flexColumn } from '../../../styles';

export const textStyle = (sx: SxProps<Theme>): SxProps<Theme> =>
	clsx(
		{
			fontFamily: 'Montserrat, sans-serif',
			textTransform: 'none',
			color: Colors.white,
			fontSize: '14px',
			fontWeight: '400',
		},
		sx
	);

export const createEventFlowStyles = () => ({
	content: {
		display: 'flex',
		flexDirection: 'column',
	},
	body: {
		maxWidth: 'none',
		pt: '70px',
		position: 'relative',
		flex: 1,
		display: 'flex',
		flexDirection: { xs: 'column-reverse', lg: 'row' },
		justifyContent: 'space-between',
		gap: 0,
	},
	backIcon: {
		width: '24px',
	},
	communitySelect: {
		'&.MuiOutlinedInput-root': {
			backgroundColor: 'transparent',
			width: 'fit-content',
			maxWidth: '100%',

			'& fieldset': {
				display: 'none',
			},
		},

		'& .MuiSelect-icon': {
			right: '10px',
			height: '18px',
			width: '18px',
		},

		'& .MuiSelect-select': {
			p: 0,
			height: 'auto',
		},

		'&:hover': {
			'&.MuiOutlinedInput-root': {
				backgroundColor: 'transparent',
			},
		},

		'&.Mui-focused': {
			'&.MuiOutlinedInput-root': {
				backgroundColor: 'transparent',
			},
		},
	},
	addCohostButton: {
		maxWidth: '180px',
		p: '8px 12px',
		color: Colors.white,
		borderWidth: 0,
		backgroundColor: Colors.white_a10,
		gap: '10px',
		height: '20px',
		boxSizing: 'content-box',
		justifyContent: 'flex-start',

		'& svg': {
			width: '18px',
			height: '18px',
			minWidth: '18px',
			minHeight: '18px',
		},

		'& .MuiTypography-root': {
			fontFamily: 'Montserrat, sans-serif',
			fontSize: '14px',
			fontWeight: 600,
			lineHeight: 'normal',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			flex: 1,
		},

		'&:hover, &:active': {
			backgroundColor: Colors.white_a8,
			color: Colors.white,
		},
	},
	privacySelect: {
		'&.MuiOutlinedInput-root': {
			borderRadius: '250px',

			'& fieldset': {
				display: 'none',
				borderRadius: '250px',
			},

			'& .MuiSelect-select': {
				p: '8px 14px 8px 12px !important',
			},
		},
	},
	timezoneSelect: {
		'& .MuiSelect-select': {
			padding: '10px 16px !important',
		},

		'&.MuiInputBase-root.Mui-disabled': {
			backgroundColor: Colors.white_a10,
		},

		'&.Mui-disabled .MuiSelect-select': {
			color: `${Colors.white_a50} !important`,
		},
	},
	repeatScheduleCheckboxRow: {
		mt: '8px',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexShrink: 0,
	},
	repeatScheduleCheckbox: {
		py: '0',
		color: Colors.white_a30,
		borderRadius: '4px',

		'&.MuiCheckbox-root.Mui-checked': {
			color: Colors.white,
		},
	},
	repeatScheduleCheckboxLabel: {
		'&.Mui-disabled .MuiTypography-root': {
			color: Colors.white_a50,
		},
	},
	repeatScheduleEditButton: {
		border: `1px solid ${Colors.transparent}`,
		padding: '0',

		'& >svg': {
			width: '20px',
			height: '20px',
		},
	},
	repeatScheduleWarningText: {
		fontFamily: 'Montserrat',
		fontWeight: 600,
		fontSize: '12px',
		lineHeight: '15px',
		textAlign: 'center',
		color: Colors.white,

		'& >span, & >a': {
			fontWeight: 900,
			color: Colors.white,
		}
	},
	addressSelect: (hasAddress?: boolean) => ({
		'& .MuiAutocomplete-inputRoot': {
			padding: '10px 16px !important',
			flexWrap: 'nowrap',

			'& .MuiInputBase-inputAdornedStart': {
				paddingLeft: '0 !important',
			},

			'& .MuiAutocomplete-endAdornment': {
				alignSelf: 'flex-start',
			},

			'& .MuiAutocomplete-input': {
				display: hasAddress ? 'none' : 'block',
			},
		},
	}),
	ticketsDisplay: {
		p: '10px 16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: '10px',
		backgroundColor: inputBackgroundColor,
		borderRadius: inputBorderRadius,
		cursor: 'pointer',
	},
	ticketsDisplayBox: {
		display: 'flex',
		alignItems: 'center',
		gap: '10px',
		flexGrow: 1,
	},
	ticketsRow: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: '4px',
		flexGrow: 1,
	},
	ticketsPlaceholder: textStyle({
		fontWeight: '600',
		lineHeight: 1.12,
	}),
	ticketsSmallInfoBox: {
		display: 'flex',
		alignItems: 'center',
		gap: '6px',
	},
	ticketsSummaryText: textStyle({
		fontWeight: '500',
		lineHeight: 1.5,
		textTransform: 'uppercase',
	}),
	activitySelect: {
		'&.MuiAutocomplete-inputRoot': {
			padding: '10px 16px',
			'& .MuiInputBase-inputAdornedStart': {
				paddingLeft: '0 !important',
			},
		},
	},
	activityBadge: {
		display: 'inline-flex !important',
		width: 'fit-content',
		borderRadius: '100px',
		gap: '8px',
		border: `1px solid ${Colors.white_a20}`,

		ml: '16px',
		mb: '10px',
	},
	backgroundBox: (src?: string) => ({
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		left: '0',
		bottom: '0',
		zIndex: { xs: 'initial', md: 0 },
		py: { xs: '21px', md: '38px' },
		px: '22px',
		width: '100%',
		height: { xs: '100%', lg: 'calc(100vh - 72px)' },
		border: src ? 'none' : `0.63px dashed ${Colors['3A3A3A']}`,
		background: src ? `url(${src}) no-repeat center center` : Colors['1A1A1A'],
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'center',
		backgroundSize: src ? 'cover' : 'auto',
		borderRadius: '3px',
	}),
	backgroundPhoto: {
		minWidth: '100%',
		height: '100%',
		aspectRatio: '538/774',
		backgroundColor: Colors['141414'],
		borderRadius: '10px',
		cursor: 'pointer',
	},
	eventTypeRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '3px',
		background: Colors['white_a5'],
		maxWidth: 'fit-content',
		borderRadius: '8px',
		padding: '3px 6px',
	},
	eventTypeItem: (selected = false) => ({
		cursor: 'pointer',
		padding: '8px 10px',
		borderRadius: '8px',
		border: selected ? `1px solid ${Colors['white_a40']}` : '1px solid transparent',
		background: selected ? Colors['white_a5'] : 'transparent',
		fontFamily: 'Montserrat, sans-serif',
		fontWeight: '600',
		fontSize: '14px',
		lineHeight: '17px',
	}),
	mainInfoBox: {
		display: 'flex',
		flexFlow: 'column nowrap',
		gap: '18px',
	},
	advancedSettingsCard: {
		cardsRow: {
			display: 'flex',
			flexFlow: 'row wrap',
			alignItems: 'flex-start',
			gap: '18px',
		},
		cardBox: {
			width: '250px',
			borderRadius: '8px',
			display: 'flex',
			flexDirection: 'column',
			padding: '10px',
			gap: '12px',
			alignItems: 'flex-start',
			backgroundColor: Colors.white_a5,
			border: `1px solid ${Colors.white_a5}`,
			userSelect: 'none',
		},
		cardIcon: {
			color: Colors.white,
			width: '32px',
			height: '32px',
		},
		cardDescriptionBox: {
			display: 'flex',
			flexDirection: 'column',
			gap: '22px',
		},
		cardDescriptionText: textStyle({
			color: Colors.white_a80,
			fontWeight: 500,
			wordBreak: 'break-word',
		}),
		cardButtonsRow: {
			display: 'flex',
			alignItems: 'center',
		},
		cardButton: textStyle({
			fontSize: '13px',
			fontWeight: 500,
			lineHeight: 1.4,
			borderRadius: '100px',
			backgroundColor: Colors.white_a10,
			padding: '6px 12px',
			gap: '6px',
			border: 'none',
		}),
		cardButtonIcon: {
			width: '16px',
			height: '16px',
		},
	},
	advancedSettingBox: flexColumn({
		gap: '2px',
	}),
	advancedSettingLabel: textStyle({
		fontSize: '16px',
		lineHeight: '28px',
		fontWeight: '600',
	}),
	advancedSettingDescription: textStyle({
		lineHeight: '21px',
		fontWeight: '500',
		color: Colors.white_a60,
		pr: '42px',
	}),
	footer: {
		position: { xs: 'static', lg: 'sticky' },
		bottom: 0,
		left: 0,
		width: '100%',
		background: Colors.black,
		py: '12px',
		px: '24px',
		borderTop: `1px solid ${Colors['white_a30']}`,
	},
	footerContent: {
		margin: '0 auto',
		display: 'flex',
		flexDirection: { xs: 'column', sm: 'row' },
		alignItems: 'center',
		justifyContent: 'center',
		gap: '12px',
		px: { xs: '15px', sm: '30px', md: '60px', lg: '125px' },
	},
	submitButton: {
		width: '158px',
		minHeight: '42px',
		padding: '12px 26px',
		fontSize: '12px',
	},
	publishButton: {
		flex: 1,
		width: '100%',
		maxWidth: '327px',
		minHeight: '42px',
		padding: '12px 32px',
	},
	imageSizeText: {
		padding: '12px 12px 12px 16px',
		fontSize: '14px',
		fontWeight: 700,
		lineHeight: '24px',
		color: Colors.white,
		fontFamily: 'Montserrat, sans-serif',
	},
	changeImageButtonWrapper: {
		maxHeight: '48px',
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		width: 'max-content',
		backgroundColor: 'rgba(0, 0, 0, 0.3)',
		borderRadius: '100px',
		display: 'flex',
		alignItems: 'center',
	},
	changeImageButton: {
		maxHeight: '48px',
		p: '12px 16px',
		color: Colors.white,
		border: 'none',
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		borderRadius: '100px',
		gap: '10px',
		zIndex: 2,
		height: '63px',

		'& .MuiTypography-root': {
			fontFamily: 'Montserrat, sans-serif',
			fontSize: '14px',
			fontWeight: 600,
			lineHeight: 'normal',
		},

		'&:hover, &:active, &.Mui-focusVisible': {
			backgroundColor: Colors['141414'],
			borderColor: Colors['252525'],
			boxShadow: 'unset',
		},
	},
});
